// From a completed RECEIVE_PALLET run to the step batch the server stores (R9.3a,
// D9-5): the implicit Received events for the pallet and every container scanned onto
// it (the interpreter does not emit those — scanning in a receiving flow *is*
// receiving), followed by the events the flow emitted (Aggregated).

import { classOf, uuidv7, type EventEnvelope, type EventType, type InspectedPayload, type ReceivedPayload, type RunState, type Value } from "@trail/core";
import type { Settings, StepBatch } from "../api/client.js";
import { t } from "../i18n.js";
import type { ResolvedScan } from "./registry.js";

export interface ScanRecord {
  containerId: string;
  scan: ResolvedScan;
  at: string;
}

export interface BatchContext {
  settings: Settings;
  goodsOwnerId: string;
  locationId: string;
  receipt: { id: string; supplierPartyId: string | null } | null;
  flow: { id: string; version: number };
  stepId: string;
  /** A tally the operator confirmed although it differed from the expectation, with the reason. */
  tallyConfirmation: { expected: number; scanned: number; reason: string } | null;
  /** Received straight onto a storage location (site setting receiving.directToStorage): the terminating scan was a bin, not the receiving area. */
  directToStorage?: boolean | undefined;
}

export function buildStepBatch(state: RunState, records: ReadonlyMap<string, ScanRecord>, ctx: BatchContext): StepBatch {
  const batchId = uuidv7();
  const pallet = state.data["pallet"] as { containerId: string } | null | undefined;
  const items = (state.data["items"] as Array<{ containerId: string }> | undefined) ?? [];
  const events: EventEnvelope[] = [];
  const palletRecord = pallet ? records.get(pallet.containerId) : undefined;
  if (palletRecord) events.push(envelope("received", palletPayload(palletRecord, ctx), palletRecord, batchId, ctx));
  for (const it of items) {
    const rec = records.get(it.containerId);
    if (!rec) continue;
    events.push(envelope("received", itemPayload(rec, pallet?.containerId ?? null, ctx), rec, batchId, ctx));
  }
  for (const em of state.emitted) {
    events.push(envelope(em.event, em.payload as Record<string, unknown>, palletRecord ?? null, batchId, ctx));
  }
  // Straight to storage (owner 2026-11-02): the pallet's last scan was a storage location, not the receiving
  // area — the pallet (or the loose items) moves there in the same batch, received at the area for the record.
  const dock = state.data["dock"];
  if (ctx.directToStorage && typeof dock === "string" && dock !== ctx.locationId) {
    const ids = pallet ? [pallet.containerId] : items.map((it) => it.containerId);
    if (ids.length) events.push(envelope("moved", { containerIds: ids, fromLocationId: ctx.locationId, toLocationId: dock, proposedLocationId: null, overrideReason: null }, null, batchId, ctx));
  }
  // Deviations are events with dispositions, never silent fixes (C4, §4.5): one Inspected
  // observation per finding, in the same batch as the receipt it belongs to.
  for (const it of items) {
    const rec = records.get(it.containerId);
    if (!rec) continue;
    for (const d of rec.scan.deviations) {
      const inspected: InspectedPayload = {
        containerId: rec.containerId,
        disposition: d === "unknown-product" ? "unknown" : d === "missing-lot" || d === "missing-best-before" ? "non_conformant" : "mismatch_quantity",
        detail: d,
        expected: null,
        observed: null,
        reason: null,
        gtin: rec.scan.input.parsed.gtin,
        lot: rec.scan.input.parsed.lot,
        expectedReceiptId: ctx.receipt?.id ?? null,
        action: "recorded",
      };
      events.push(envelope("inspected", inspected, rec, batchId, ctx));
    }
    // What the operator entered at the pallet is a finding too: the office sees that a
    // duplicate identity was told apart by a serial, or that a date came from a person.
    const entered = rec.scan.entered;
    if (entered?.serial) {
      const inspected: InspectedPayload = { containerId: rec.containerId, disposition: "mismatch_instance", detail: "collision-serial", expected: null, observed: entered.serial, reason: "serial entered at receiving", gtin: rec.scan.input.parsed.gtin, lot: rec.scan.input.parsed.lot, expectedReceiptId: ctx.receipt?.id ?? null, action: "recorded" };
      events.push(envelope("inspected", inspected, rec, batchId, ctx));
    }
    if (entered?.bestBefore) {
      const inspected: InspectedPayload = { containerId: rec.containerId, disposition: "non_conformant", detail: "missing-best-before", expected: null, observed: entered.bestBefore, reason: `entered:${entered.bestBeforeSource ?? "unknown"}`, gtin: rec.scan.input.parsed.gtin, lot: rec.scan.input.parsed.lot, expectedReceiptId: ctx.receipt?.id ?? null, action: "recorded" };
      events.push(envelope("inspected", inspected, rec, batchId, ctx));
    }
  }
  if (ctx.tallyConfirmation && palletRecord) {
    const inspected: InspectedPayload = {
      containerId: palletRecord.containerId,
      disposition: "mismatch_quantity",
      detail: "tally",
      expected: ctx.tallyConfirmation.expected,
      observed: ctx.tallyConfirmation.scanned,
      reason: ctx.tallyConfirmation.reason,
      gtin: null,
      lot: null,
      expectedReceiptId: ctx.receipt?.id ?? null,
      action: "recorded",
    };
    events.push(envelope("inspected", inspected, palletRecord, batchId, ctx));
  }
  return { flowId: ctx.flow.id, flowVersion: ctx.flow.version, stepId: ctx.stepId, events, effects: [] };
}

function envelope(type: EventType, payload: Record<string, unknown>, rec: ScanRecord | null, batchId: string, ctx: BatchContext): EventEnvelope {
  return {
    eventId: uuidv7(),
    tenantId: ctx.settings.tenantId,
    goodsOwnerId: ctx.goodsOwnerId,
    type,
    class: classOf(type),
    schemaVersion: 1,
    occurredAt: rec?.at ?? new Date().toISOString(),
    actorId: ctx.settings.actorId,
    deviceId: ctx.settings.deviceId,
    capture: rec ? (rec.scan.source === "manual" ? "manual" : "barcode") : "system",
    captureDetail: rec ? [rec.scan.symbologyId, rec.scan.source ?? "wedge", rec.scan.assembled ? "assembled" : null, rec.scan.note ? `manuell: ${rec.scan.note}` : null, rec.scan.entered?.quantity !== undefined ? (rec.scan.quantity?.source === "standard" ? `antal: standard ${rec.scan.entered.quantityStandard} bekräftat` : `antal: ${rec.scan.entered.quantity} för hand${rec.scan.entered.quantityStandard ? ` (standard ${rec.scan.entered.quantityStandard})` : ""}`) : null].filter(Boolean).join(" ") : null,
    readPoint: ctx.locationId,
    bizLocation: ctx.locationId,
    flowId: ctx.flow.id,
    flowVersion: ctx.flow.version,
    batchId,
    payload,
  };
}

function palletPayload(rec: ScanRecord, ctx: BatchContext): ReceivedPayload {
  const e = rec.scan.input.parsed;
  const sscc = e.sscc!;
  return {
    containerId: rec.containerId,
    kind: "logistic-unit",
    identity: { scheme: "sscc", key: `sscc:00=${sscc}`, ai: { "00": sscc }, origin: rec.scan.issuedHere ? "tenant" : "supplier" },
    productId: null,
    gtin: null,
    packagingLevel: "pallet",
    lot: null,
    serial: null,
    sscc,
    quantity: { value: "1", uom: "EA", source: "scanned" },
    locationId: ctx.locationId,
    parentContainerId: null,
    supplierPartyId: ctx.receipt?.supplierPartyId ?? null,
    expectedReceiptId: ctx.receipt?.id ?? null,
    deliveryId: null,
    stated: false,
  };
}

function itemPayload(rec: ScanRecord, parentContainerId: string | null, ctx: BatchContext): ReceivedPayload {
  const e = rec.scan.input.parsed;
  const resolved = rec.scan.input.resolved;
  const key = resolved.as === "container" ? resolved.identityKey : null;
  const level = rec.scan.gtinInfo?.level ?? null;
  const scheme = key?.startsWith("gtin+lot+serial:") ? "gtin+lot+serial" : key?.startsWith("gtin+serial:") ? "gtin+serial" : "gtin+lot";
  // A bulk product (owner 2026-11-03): a quantity of the product with no identity — kind "loose". It gets a
  // technical lot, date and time of the scan, that the ERP's batch row for this receipt is created with and
  // the mirror links by; the operator never sees it as a batch.
  const bulk = rec.scan.bulk === true;
  const techLot = technicalLot(rec.at);
  return {
    containerId: rec.containerId,
    kind: bulk ? "loose" : "trade-item",
    identity: bulk ? { scheme: "gtin+lot", key: null, ai: e.gtin ? { "01": e.gtin } : {}, origin: "supplier" } : { scheme, key, ai: { ...e.ai }, origin: "supplier" },
    productId: rec.scan.product?.id ?? null,
    gtin: e.gtin,
    packagingLevel: bulk ? null : level === "each" || level === "inner" || level === "case" || level === "pallet" ? level : null,
    lot: bulk ? { number: techLot, bestBefore: null, expiry: null, productionDate: null, packagingDate: null } : e.lot ? { number: e.lot.slice(0, 20), bestBefore: e.bestBefore, expiry: e.expiry, productionDate: e.productionDate, packagingDate: e.packagingDate } : null,
    serial: e.serial,
    sscc: null,
    quantity: rec.scan.quantity ?? { value: "1", uom: "EA", source: "inferred" },
    locationId: ctx.locationId,
    parentContainerId,
    supplierPartyId: ctx.receipt?.supplierPartyId ?? null,
    expectedReceiptId: ctx.receipt?.id ?? null,
    deliveryId: null,
    stated: false,
  };
}

/** A step batch for a flow whose events are all explicit emit_event steps (put-away,
 *  move): the interpreter's emitted events wrapped in envelopes, nothing implicit. */
export function buildEmittedBatch(state: RunState, ctx: { settings: Settings; goodsOwnerId: string; readPoint: string | null; flow: { id: string; version: number }; stepId: string; captureDetail: string | null; occurredAt: string }): StepBatch {
  const batchId = uuidv7();
  const events: EventEnvelope[] = state.emitted.map((em) => ({
    eventId: uuidv7(),
    tenantId: ctx.settings.tenantId,
    goodsOwnerId: ctx.goodsOwnerId,
    type: em.event,
    class: classOf(em.event),
    schemaVersion: 1,
    occurredAt: ctx.occurredAt,
    actorId: ctx.settings.actorId,
    deviceId: ctx.settings.deviceId,
    capture: "barcode",
    captureDetail: ctx.captureDetail,
    readPoint: ctx.readPoint,
    bizLocation: ctx.readPoint,
    flowId: ctx.flow.id,
    flowVersion: ctx.flow.version,
    batchId,
    payload: em.payload as Record<string, unknown>,
  }));
  return { flowId: ctx.flow.id, flowVersion: ctx.flow.version, stepId: ctx.stepId, events, effects: [] };
}

/** Sum of scanned quantities per product and unit for this run — the per-pallet picture. */
export interface TallyRow {
  productId: string | null;
  product: string;
  sku: string | null;
  gtin: string | null;
  containers: number;
  sums: Map<string, number>;
}

export function tally(records: Iterable<ScanRecord>): Map<string, TallyRow> {
  const out = new Map<string, TallyRow>();
  for (const rec of records) {
    const resolved = rec.scan.input.resolved;
    if (resolved.as !== "container" || resolved.containerKind === "logistic-unit") continue;
    const key = rec.scan.product?.id ?? `gtin:${rec.scan.input.parsed.gtin ?? "?"}`;
    const row = out.get(key) ?? { productId: rec.scan.product?.id ?? null, product: rec.scan.product?.name ?? `${t("label.unknownProduct")} · GTIN ${rec.scan.input.parsed.gtin ?? "?"}`, sku: rec.scan.product?.sku ?? null, gtin: rec.scan.input.parsed.gtin, containers: 0, sums: new Map<string, number>() };
    row.containers += 1;
    const q = rec.scan.quantity;
    if (q) row.sums.set(q.uom, (row.sums.get(q.uom) ?? 0) + Number(q.value));
    out.set(key, row);
  }
  return out;
}

/** The technical lot of a bulk receipt: date and time of the scan, local, "YYMMDD-HHMMSS" (owner 2026-11-03: "dagens datum + löpnr"). */
export function technicalLot(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${String(d.getFullYear()).slice(2)}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

export function asValue(v: unknown): Value {
  return JSON.parse(JSON.stringify(v ?? null)) as Value;
}
